import * as Schema from 'generated/graphql/schema';

export const convertMultiplierOffset = (
  multiplier: number,
  offset: number,
  analogRange: Schema.AnalogInputRange,
): { sensorMin: number; sensorMax: number } => {
  const unitMaxMin = parseAnalogRange(analogRange);
  const sensorMin = offset + multiplier * (unitMaxMin.min * 1000);
  const sensorMax = sensorMin + multiplier * (unitMaxMin.max * 1000 - unitMaxMin.min * 1000);
  return { sensorMin, sensorMax };
};

export const convertSensorMinMax = (
  sensorMin: number,
  sensorMax: number,
  analogRange: Schema.AnalogInputRange,
): { dataMultiplier: number; analogOffset: number } => {
  const unitMaxMin = parseAnalogRange(analogRange);
  const dataMultiplier = (sensorMax - sensorMin) / (unitMaxMin.max * 1000 - unitMaxMin.min * 1000);
  return { dataMultiplier, analogOffset: sensorMin - dataMultiplier * (unitMaxMin.min * 1000) };
};

export const convertSensorMinMaxAndMultiplier = (
  sensorMin: number,
  sensorMax: number,
  analogRange: Schema.AnalogInputRange,
  multiplier: number,
) => {
  let { dataMultiplier, analogOffset } = convertSensorMinMax(sensorMin, sensorMax, analogRange);
  dataMultiplier *= multiplier;
  analogOffset *= multiplier;
  // In other to recover the min/max we also need to save the extra multiplier
  return { dataMultiplier, analogOffset, multiplier };
};

export const recoverMinMaxWithMultiplier = (
  dataMultiplier: number,
  offset: number,
  analogRange: Schema.AnalogInputRange,
  multiplier: number,
) => {
  dataMultiplier *= 1 / multiplier;
  offset *= 1 / multiplier;
  if (isNaN(dataMultiplier)) {
    dataMultiplier = 0;
  }
  if (isNaN(offset)) {
    offset = 0;
  }
  return convertMultiplierOffset(dataMultiplier, offset, analogRange);
};

export const parseAnalogRange = (range: Schema.AnalogInputRange): { min: number; max: number } => {
  switch (range) {
    case Schema.AnalogInputRange.NEGATIVE_TEN_TO_TEN_VOLTS:
      return { min: -10, max: 10 };
    case Schema.AnalogInputRange.ZERO_TO_TEN_VOLTS:
      return { min: 0, max: 10 };
    case Schema.AnalogInputRange.ZERO_TO_FIVE_VOLTS:
      return { min: 0, max: 5 };
    case Schema.AnalogInputRange.NEGATIVE_FIVE_TO_FIVE_VOLTS:
      return { min: -5, max: 5 };
    case Schema.AnalogInputRange.ZERO_TO_TWENTY_MILLIAMPS:
      return { min: 0, max: 20 };
    case Schema.AnalogInputRange.ZERO_TO_TWENTYFOUR_MILLIAMPS:
      return { min: 0, max: 24 };
    case Schema.AnalogInputRange.FOUR_TO_TWENTY_MILLIAMPS:
      return { min: 4, max: 20 };
    case Schema.AnalogInputRange.NEGATIVE_TWENTYFIVE_TO_TWENTYFIVE_MILLIAMPS:
      return { min: -25, max: 25 };
    default:
      // TODO: Removed the below assertion, as we actually can end up here when changing a sensor from another type to 'Measurement'.
      // Not sure if this should be handled differently
      // Types.assertUnreachable(range);
      return { min: 4, max: 20 };
  }
};
